import React, { useRef, useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import {
  isAgencyAdmin,
  isAgencyUser,
  isRolePlatformAdmin,
  token,
  userAgencyObject,
  userCompanyObject,
} from "../../../modules/auth";
import { canEditPreferredProgrammes, isCompanyAdmin, isCompanyUser } from "../../../modules/permissions";
import PreferredProgrammeFilters from "../../../Components/PreferredProgrammes/PreferredProgrammeFilters";
import {
  fetchHotelGroup,
  fetchSnapshotBasedOnHotelGroupsReport,
  fetchSnapshotReport,
  snapshotReportHotelGroupUnload,
} from "../../../redux/actions";
import * as ROUTES from "../../../Router/venue_routes";
import Loading from "../../../Components/Loading";
import { withRouter, Link } from "react-router-dom";
import PreferredProgrammeSnapshotReportTable
  from "../../../Components/PreferredProgrammes/PreferredProgrammeSnapshotReportTable";
import NotFound from "../../NotFound";
import { HOTEL_GROUP_TYPE_CODES } from "../../../constants";

/**
 * PreferredProgrammeViewContainer
 *
 * Main container component for viewing preferred programme details and hierarchical navigation.
 * Displays hotel group information, snapshot reports (YTD statistics), and provides filters
 * for navigating through the programme hierarchy (Agency -> Company -> Group -> Brand -> Venue).
 *
 * Key features:
 * - Hierarchical navigation through preferred programme structure
 * - Year-to-date snapshot reporting with filtering capabilities
 * - Dynamic breadcrumb navigation based on current position in hierarchy
 * - Support for both "My Programme" view and general programme browsing
 */

// Key used to extract year-to-date data from snapshot reports
const SNAPSHOT_KEY = "yearToDate";

let PreferredProgrammeViewContainer = ({
                                         groupId,
                                         token,
                                         hotelGroup,
                                         hotelGroupNotFound,
                                         fetchHotelGroup,
                                         // isMyProgramme,
                                         myProgrammeId,
                                         history,
                                         isFetchingHotelGroup,
                                         isFetchingSnapshotForHotelGroupsReport,
                                         // fetchSnapshotReport,
                                         fetchSnapshotBasedOnHotelGroupsReport,
                                         snapshotReport,
                                         snapshotReportForHotelGroups,
                                         programmeId,
                                         match,
                                         isCompanyAdmin,
                                         isCompanyUser,
                                         snapshotReportHotelGroupUnload,
                                         canEditPreferredProgrammes,
                                       }) => {
  // Ref to track the last query params used for snapshot fetch (prevents duplicate API calls)
  const snapshotLastQueryParamsRef = useRef(null);

  // Filter selection state - tracks user selections in the filter dropdowns
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);

  // Current context state - derived from hotelGroup data to populate filter options
  const [company, setCompany] = useState(null);
  const [agency, setAgency] = useState(null);

  // Venue IDs to limit snapshot report to (when filtering by specific venues)
  const [snapshotVenues, setSnapshotVenues] = useState([]);

  // Snapshot report state - stores different versions of the YTD snapshot data
  const [snapShot, setSnapshot] = useState(null);           // Basic snapshot from snapshotReport
  const [snapShotHG, setSnapshotHG] = useState(null);       // Hotel group-specific snapshot
  const [snapShotToDisplay, setSnapShotToDisplay] = useState(null);  // Currently displayed snapshot

  const isDirect = String(groupId).indexOf('direct') !== -1;
  groupId = isDirect ? groupId.replace('direct-', '') : groupId;


  // Extract programme ID from URL route params
  const programmeIdFromRoute = match?.params?.programme ?? null;

  // Determine if viewing the user's own preferred programme (for "My Programme" functionality)
  const isMyProgramme = useMemo(() => Number(programmeId) === Number(myProgrammeId), [programmeId, myProgrammeId]);

  // Find the top-level programme for breadcrumb navigation
  // Falls back to hotelGroup itself if it's a preferred programme, or a generic "Back to Programme" link
  const topLevelProgramme = useMemo(() =>
      hotelGroup?.programmes?.filter(p => Number(p.id) === Number(programmeId))[0] ??
      (hotelGroup?.hotelGroupType?.code === HOTEL_GROUP_TYPE_CODES.PREFERRED_PROGRAMME ?
        hotelGroup :
        { name: "Back to Programme" })
    , [hotelGroup, programmeId]);

  // Alias for hotelGroup used throughout the component
  const group = hotelGroup;

  // Cleanup effect - reset snapshot state when component unmounts
  useEffect(() => {
    return () => {
      setSnapshotHG(null);
      setSnapshot(null);
      setSnapShotToDisplay(null);
      snapshotReportHotelGroupUnload();
    };
  }, []);

  // Fetch hotel group data when groupId changes
  useEffect(() => {
    fetchHotelGroup(groupId, token, programmeIdFromRoute, true);
  }, [groupId]);

  // Extract year-to-date snapshot from the basic snapshot report
  useEffect(() => {
    if (snapshotReport) {
      snapshotReport.find(report => {
        let yearToDate = report.find(report => {
          return report.key === "yearToDate";
        });
        setSnapshot(yearToDate);
      });
    }
  }, [snapshotReport]);

  // Extract year-to-date snapshot from hotel groups report and set as display snapshot
  useEffect(() => {
    if (snapshotReportForHotelGroups) {
      snapshotReportForHotelGroups.find(report => {
        let yearToDate = report.find(report => {
          return report.key === SNAPSHOT_KEY;
        });
        setSnapshotHG(yearToDate);
        setSnapShotToDisplay(yearToDate);
      });
    }
  }, [snapshotReportForHotelGroups]);

  /**
   * Navigate to a different group/venue within the preferred programme hierarchy
   * @param {number} groupId - The hotel group or venue ID to navigate to
   * @param {number} programmeId - The preferred programme ID for context
   * @param {boolean} venue - If true, navigates to venue-specific view
   */
  let redirect = (groupId, programmeId, venue = false) => {
    const route = venue ? ROUTES.FRONTEND.PREFERRED_PROGRAMMES.VIEW_VENUE : ROUTES.FRONTEND.PREFERRED_PROGRAMMES.VIEW;
    history.push(
      route.replace(
        ":id",
        groupId,
      ).replace(":programme", programmeId),
    );

    // Reset child selections when navigating to a new group
    setSelectedGroup(null);
    setSelectedBrand(null);
    setSnapshot(null);
  };

  // Handle agency filter selection - navigate to agency's preferred programme
  useEffect(() => {
    if (selectedAgency) {
      redirect(
        selectedAgency.preferredProgramme,
        selectedAgency.preferredProgramme,
      );
      setSelectedCompany(null);
    }
  }, [selectedAgency]);

  // Handle company filter selection - navigate to company's or agency's preferred programme
  useEffect(() => {
    if (selectedCompany) {
      if (selectedCompany.value === "" && selectedAgency) {
        // Empty company selection with agency = go back to agency level
        redirect(
          selectedAgency.preferredProgramme,
          selectedAgency.preferredProgramme,
        );
      } else {
        redirect(
          selectedCompany.preferredProgramme,
          selectedCompany.preferredProgramme,
        );
      }
    }
  }, [selectedCompany]);

  // Handle group filter selection - navigate to selected hotel group
  useEffect(() => {
    if (selectedGroup) {
      // Determine the programme context based on current filter state
      let programme = selectedCompany
        ? selectedCompany.preferredProgramme
        : selectedAgency
          ? selectedAgency.preferredProgramme
          : myProgrammeId;

      if (hotelGroup && hotelGroup.groupType.code === HOTEL_GROUP_TYPE_CODES.PREFERRED_PROGRAMME) {
        redirect(selectedGroup.value, hotelGroup.id);
      } else {
        redirect(selectedGroup.value, programmeId ? programmeId : programme);
      }
    }
  }, [selectedGroup]);

  // Handle brand filter selection - navigate to selected brand (unless it's "direct venues")
  useEffect(() => {
    if (selectedBrand) {
      let programme = selectedCompany
        ? selectedCompany.preferredProgramme
        : selectedAgency
          ? selectedAgency.preferredProgramme
          : myProgrammeId;
      redirect(selectedBrand.value, programmeId ? programmeId : programme);
    }
  }, [selectedBrand]);

  // Handle venue filter selection - navigate to venue-specific view
  useEffect(() => {
    if (selectedVenue) {
      let programme = selectedCompany
        ? selectedCompany.preferredProgramme
        : selectedAgency
          ? selectedAgency.preferredProgramme
          : myProgrammeId;
      redirect(
        selectedVenue.value,
        programmeId ? programmeId : programme,
        true,
      );
    }
  }, [selectedVenue]);

  // Derive company and agency context from hotelGroup data for filter population
  useEffect(() => {
    if (hotelGroup) {
      // Set company context if hotelGroup belongs to a company
      if (hotelGroup.company) {
        setCompany({
          label: hotelGroup.company.name,
          value: hotelGroup.company.id,
          text: hotelGroup.company.name,
          preferredProgramme: hotelGroup.id,
        });
      }
      // Set agency context - can come from hotelGroup directly or through company
      if (
        hotelGroup.agency ||
        (hotelGroup.company && hotelGroup.company.agency)
      ) {
        setAgency({
          label:
            hotelGroup.company && hotelGroup.company.agency
              ? hotelGroup.company.agency.name
              : hotelGroup.agency.name,
          value:
            hotelGroup.company && hotelGroup.company.agency
              ? hotelGroup.company.agency.id
              : hotelGroup.agency.id,
          text:
            hotelGroup.company && hotelGroup.company.agency
              ? hotelGroup.company.agency.name
              : hotelGroup.agency.name,
          preferredProgramme:
            hotelGroup.company && hotelGroup.company.agency
              ? hotelGroup.company.agency.preferred_programme_id
              : hotelGroup.id,
        });
        // If viewing at agency level, clear company selection
        if (hotelGroup.agency) {
          setSelectedCompany(null);
          setCompany(null);
        }
      }
    }
  }, [hotelGroup]);

  /**
   * Fetch snapshot report data when hotelGroup or filter criteria change.
   * Builds query params based on current viewing context and triggers API call.
   * Uses ref to prevent duplicate fetches with identical parameters.
   */
  useEffect(() => {
    if (!hotelGroup) return;

    // Determine the preferred programme ID - use programmeId from route, or myProgrammeId for "My Programme" view
    const preferredProgrammeId = programmeId || myProgrammeId;

    // If the current hotelGroup IS the preferred programme, use its ID
    const effectivePreferredProgrammeId = hotelGroup.groupType?.code === HOTEL_GROUP_TYPE_CODES.PREFERRED_PROGRAMME
      ? hotelGroup.id
      : preferredProgrammeId;

    if (!effectivePreferredProgrammeId) return;

    // Don't send hotelGroups param when viewing the top-level preferred programme itself
    // (the groupIdsForReporting field contains child group IDs, not the programme ID)
    const isViewingTopLevelProgramme = hotelGroup.groupType?.code === HOTEL_GROUP_TYPE_CODES.PREFERRED_PROGRAMME;

    // Build query params for snapshot API request
    let queryParams = {
      ...(!isViewingTopLevelProgramme ? { hotelGroups: hotelGroup.id } : {}),
      preferredProgramme: effectivePreferredProgrammeId,
      ...(snapshotVenues?.length ? { limitToVenues: snapshotVenues } : {}),
      ...(isDirect ? { direct: true } : {}),
    };

    // Find matching programme to add agency/company context if available
    let programme = hotelGroup.programmes
      ? hotelGroup.programmes.find(prog => {
        return parseInt(prog.id) === parseInt(programmeId);
      })
      : null;
    if (programme && programme.agency) {
      queryParams = { ...queryParams, agency: programme.agency.id };
    }
    if (programme && programme.company) {
      queryParams = { ...queryParams, company: programme.company.id };
    }

    // Only fetch if params have changed (prevents duplicate API calls)
    const queryParamsStr = JSON.stringify(queryParams);
    if (!isFetchingSnapshotForHotelGroupsReport && snapshotLastQueryParamsRef.current !== queryParamsStr) {
      snapshotLastQueryParamsRef.current = queryParamsStr;
      fetchSnapshotBasedOnHotelGroupsReport(token, queryParams);
    }
  }, [hotelGroup, fetchSnapshotBasedOnHotelGroupsReport, token, snapShotHG, snapshotVenues, programmeId, myProgrammeId]);

  // Show loading state while fetching hotel group data
  if (!group || isFetchingHotelGroup) {
    return <Loading />;
  }

  /**
   * Format and display address fields as a list
   * Returns "Not Provided" if all fields are empty
   */
  let displayAddress = (
    address1,
    address2,
    address3,
    city,
    county,
    postcode,
    country,
  ) => {
    if (
      !address1 &&
      !address2 &&
      !address3 &&
      !city &&
      !county &&
      !postcode &&
      !country
    ) {
      return <div className={"py-2"}>Not Provided</div>;
    }
    return (
      <div className={"py-2"}>
        <ul className="list-reset mb-3">
          {address1 && <li className="mb-1">{address1}</li>}
          {address2 && <li className="mb-1">{address2}</li>}
          {address3 && <li className="mb-1">{address3}</li>}
          {city && <li className="mb-1">{city}</li>}
          {county && <li className="mb-1">{county}</li>}
          {postcode && <li className="mb-1">{postcode}</li>}
          {country && <li className="mb-1">{country}</li>}
        </ul>
      </div>
    );
  };

  // Find programme-specific override data (custom account manager, phone, email, description, etc.)
  // Overrides allow preferred programmes to customize how hotel groups appear within their programme
  let override =
    hotelGroup?.overrides && programmeId
      ? hotelGroup.overrides.find(function(override) {
        return (
          parseInt(override.preferred_programme_id) === parseInt(programmeId)
        );
      })
      : null;

  override = override || {};

  // Show 404 page if hotel group was not found
  if (hotelGroupNotFound) {
    return <NotFound />;
  }

  let groupName = group.agency ? group.agency.name + ' Agency Preferred Programme' : (group.company ?  group.company.name + ' Company Preferred Programme' : group.name)
  let topLevelProgrammeName =  topLevelProgramme.agency ? topLevelProgramme.agency.name + ' Agency Preferred Programme' : (topLevelProgramme.company ?  topLevelProgramme.company.name + ' Company Preferred Programme' : group.name)
  /**
   * Render the preferred programme view with:
   * 1. Header section with breadcrumb navigation and edit link
   * 2. Filter dropdowns for navigating the programme hierarchy
   * 3. Two-column layout:
   *    - Left: Group details (image, contact info, address, profile) OR aggregated programme message
   *    - Right: Year-to-date snapshot statistics table
   */
  return (
    <div>
      {/* Header section with breadcrumb and title */}
      <div className="border-b border-gray-200 bg-white p-5">
        {/* Breadcrumb navigation - hidden for "my-preferred-programme" route */}
        {!match.path.includes("my-preferred-programme") ? (
          <div>
            {!!programmeId ? (
              <>
                {isMyProgramme ? (
                  <Link
                    to={ROUTES.FRONTEND.PREFERRED_PROGRAMMES.MY}
                    className={"font-bold text-black "}
                  >
                    My Programme {" > "}
                  </Link>
                ) : (
                  <Link
                    to={ROUTES.FRONTEND.PREFERRED_PROGRAMMES.VIEW.replace(
                      ":id",
                      programmeId,
                    ).replace(":programme", "")}
                    className={"font-bold text-black "}
                  >
                    {topLevelProgrammeName} {" > "}
                  </Link>
                )}
                {hotelGroup.parentGroup && String(hotelGroup.parentGroup.id) !== String(programmeId) ? (
                  <Link
                    to={ROUTES.FRONTEND.PREFERRED_PROGRAMMES.VIEW.replace(
                      ":id",
                      hotelGroup.parentGroup.id,
                    ).replace(":programme", programmeId)}
                    className={"font-bold text-black "}
                  >
                    {hotelGroup.parentGroup.name} {" > "}
                  </Link>
                ) : null}
                {isDirect ? (
                  <Link
                    to={ROUTES.FRONTEND.PREFERRED_PROGRAMMES.VIEW.replace(
                      ":id",
                      hotelGroup.id,
                    ).replace(":programme", programmeId)}
                    className={"font-bold text-black "}
                  >
                    {groupName} {" > "}
                  </Link>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
        <div className={"flex items-center"}>
          <h1 className={"py-3"}>
            {groupName ? groupName : ""}{" "}
          </h1>
          {canEditPreferredProgrammes && hotelGroup.groupType.code !== HOTEL_GROUP_TYPE_CODES.PREFERRED_PROGRAMME ? (
            <Link
              to={ROUTES.FRONTEND.PREFERRED_PROGRAMMES.EDIT.replace(
                ":id",
                hotelGroup.id,
              ).replace(":programme", programmeId)}
              className={"text-black"}
            >
              <i className="fal fa-edit px-3" />
            </Link>
          ) : null}
        </div>

        {/* Hierarchical filter dropdowns for navigating programme structure */}
        <PreferredProgrammeFilters
          selectedAgency={selectedAgency}
          setSelectedAgency={setSelectedAgency}
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          setSelectedGroup={setSelectedGroup}
          selectedGroup={selectedGroup}
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
          selectedVenue={selectedVenue}
          setSelectedVenue={setSelectedVenue}
          setSnapshotVenues={setSnapshotVenues}
          company={true} // Setting true hides the company filter dropdown
          agency={agency}
          hotelGroup={hotelGroup}
          showGroupFilters={true}
          isMyProgramme={isMyProgramme}
          isDirect={isDirect}
          labelPrefix={"Filter by"}
        />
      </div>

      {/* Main content section - two column layout */}
      <div className="border-b border-gray-200  bg-white p-5">
        <div className={"row"}>
          {/* Left column: Show aggregated message for company users, otherwise show group details */}
          {(isCompanyAdmin || isCompanyUser) && !isMyProgramme ? (
            <div className={`col col-w-1/2`}>
              <div className={"w-full"}>
                <p className={"bg-primary mb-5 text-white p-4"}>
                  <strong>
                    Total across all groups in your preferred programme.
                  </strong>
                </p>
              </div>
            </div>
          ) : (
            <div className={`col col-w-1/2`}>
              {group.image ? (
                <div className="bg-white py-2 justify-center">
                  <img
                    alt={group?.name + " image"}
                    src={`${process.env.REACT_APP_API}/api/hotel-groups/${group.id}/image`}
                    style={{
                      maxWidth: 96,
                    }}
                  />
                </div>
              ) : null}

              <h3>{group.name}</h3>
              <div className={"py-2"}>
                <table className={"edge-table-no-stripe ml-0"}>
                  <tbody>
                  <tr>
                    <th className={"w-1/3"}>Account Manager:</th>
                    <td>
                      {override?.account_manager
                        ? override?.account_manager
                        : group?.manager
                          ? `(${group.manager.first_name} ${group.manager.surname})`
                          : "Not Provided"}
                    </td>
                  </tr>
                  <tr>
                    <th>Telephone:</th>
                    <td>
                      {override?.phone_number
                        ? override.phone_number
                        : group?.phone_number
                          ? group.phone_number
                          : "Not Provided"}
                    </td>
                  </tr>
                  <tr>
                    <th>Email Address:</th>
                    <td>
                      {override?.email
                        ? override.email
                        : group?.email_address
                          ? group.email_address
                          : "Not Provided"}
                    </td>
                  </tr>
                  {
                    group && group.groupType && group.groupType.code !== 'PPR'  &&  group.groupType.code !== 'CHG'
                    ?
                      <tr>
                        <th>Website:</th>
                        <td>
                          { group?.website
                            ? group.website
                            : "Not Provided"}
                        </td>
                      </tr>
                      :
                      null
                  }

                  <tr
                    hidden={
                      group && group.groupType
                        ? group.groupType.code === "CHG"
                        : false
                    }
                  >
                    <th>Address:</th>
                    <td>
                      {isMyProgramme
                        ? displayAddress(
                          group?.address?.address1,
                          group?.address?.address2,
                          group?.address?.address3,
                          group?.address?.city,
                          group?.address?.county,
                          group?.address?.postcode,
                          group?.address?.country?.name,
                        )
                        : displayAddress(
                          group?.address1,
                          group?.address2,
                          group?.address3,
                          group?.address?.city,
                          group?.address?.county,
                          group?.address?.postcode,
                          group?.address?.country?.name,
                        )}
                    </td>
                  </tr>
                  {override?.description || group?.description ? (
                    <tr>
                      <th className={"flex"}>Profile</th>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {override?.description
                          ? override.description
                          : group?.description
                            ? group.description
                            : null}
                      </td>
                    </tr>
                  ) : null}
                  {override?.educational_rates ? (
                    <tr>
                      <th className={"flex"}>Educational Rates</th>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {override.educational_rates}
                      </td>
                    </tr>
                  ) : null}
                  {override?.preferred_packages ? (
                    <tr>
                      <th className={"flex"}>Preferred Packages</th>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {override.preferred_packages}
                      </td>
                    </tr>
                  ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Right column: Year-to-date snapshot statistics */}
          <div className={`col col-w-1/2`}>
            <h3 className={"mb-2"}>Snapshot (YTD)</h3>
            {!isFetchingSnapshotForHotelGroupsReport && snapShotToDisplay !== null ? (
              <PreferredProgrammeSnapshotReportTable
                tableData={snapShotToDisplay}
              />
            ) : (
              <Loading />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * Map Redux state to component props
 * Determines groupId based on whether viewing "My Programme" or a specific programme
 */
const mapStateToProps = (state, props) => {
  let isMyProgramme = props?.isMyProgramme || false;
  const userAgency = userAgencyObject(state);
  const userCompany = userCompanyObject(state);

  // For "My Programme" view, use the user's agency/company preferred programme ID
  // Otherwise, use the ID from the URL route params
  let groupId = isMyProgramme
    ? userAgency
      ? userAgency?.preferred_programme_id
      : userCompany?.preferred_programme_id
    : props.match.params.id;

  return {
    ...state.hotelGroup,            // hotelGroup, isFetchingHotelGroup, hotelGroupNotFound
    ...state.snapshotReport,        // snapshotReport
    ...state.snapshotReportForHotelGroups, // snapshotReportForHotelGroups, isFetchingSnapshotForHotelGroupsReport
    token: token(state),
    isAgencyAdmin: isAgencyAdmin(state),
    isAgencyUser: isAgencyUser(state),
    isCompanyAdmin: isCompanyAdmin(state.auth),
    isCompanyUser: isCompanyUser(state.auth),
    isRolePlatformAdmin: isRolePlatformAdmin(state),
    canEditPreferredProgrammes: canEditPreferredProgrammes(state.auth),
    groupId: groupId,
    programmeId: props.match.params.programme,
    // User's own preferred programme ID (for "My Programme" feature)
    myProgrammeId: userAgency
      ? userAgency?.preferred_programme_id
      : userCompany
        ? userCompany?.preferred_programme_id
        : null,
  };
};

// Redux action creators bound to dispatch
const mapDispatchToProps = {
  fetchHotelGroup,                        // Fetch hotel group details
  fetchSnapshotReport,                    // Fetch basic snapshot report
  fetchSnapshotBasedOnHotelGroupsReport,  // Fetch snapshot filtered by hotel groups
  snapshotReportHotelGroupUnload,         // Clear snapshot report state
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PreferredProgrammeViewContainer),
);
